import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DietaryPreference } from "../../../../../../../types/dietary";
import { getDietaryPreferencesWithSelection } from "../../../../../../../services/dietaryService";
import { getDisplayDietaryName, getDisplayDietaryDescription } from "../../../../../../../utils/dietaryHelper";

interface DietaryPreferencesTabProps {
  itemId: string;
  selectedDietaryPreferences?: string[];
  onDietaryPreferencesChange?: (preferences: string[]) => void;
  onOriginalDietariesLoaded?: (ids: string[]) => void;
}

const DietaryPreferencesTab: React.FC<DietaryPreferencesTabProps> = ({
  itemId,
  selectedDietaryPreferences = [],
  onDietaryPreferencesChange,
  onOriginalDietariesLoaded,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [dietaryPreferences, setDietaryPreferences] = useState<DietaryPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Render loading, error, preferences list, None
  useEffect(() => {
    const fetchDietaryPreferences = async () => {
      try {
        setIsLoading(true);
        const preferences = await getDietaryPreferencesWithSelection(itemId);
        setDietaryPreferences(preferences);
        // Initialize selection in parent from already_selected if parent has none yet
        if (onDietaryPreferencesChange && (selectedDietaryPreferences?.length ?? 0) === 0) {
          const alreadySelected = (preferences || [])
            .filter((p) => p.already_selected)
            .map((p) => p.id);
          onDietaryPreferencesChange(alreadySelected);
          // Notify parent about original selections for delta calculation
          onOriginalDietariesLoaded?.(alreadySelected);
        }
        setError(null);
      } catch (err) {
        setError(t("menuItems.errors.dietaryPreferencesFetchFailed", "Failed to load dietary preferences"));
      } finally {
        setIsLoading(false);
      }
    };

    if (itemId) {
      fetchDietaryPreferences();
    }
  }, [itemId, t]);
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">{t("common.loading")}</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }
  if (dietaryPreferences.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          {t("menuItems.noDietaryPreferences", "No dietary preferences available")}
        </p>
      </div>
    );
  }
  const handleToggleDietaryPreference = (
    e: React.MouseEvent<HTMLButtonElement>,
    preferenceId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onDietaryPreferencesChange) return;

    const isSelected = selectedDietaryPreferences.includes(preferenceId);
    const updatedPreferences = isSelected
      ? selectedDietaryPreferences.filter((id) => id !== preferenceId)
      : [...selectedDietaryPreferences, preferenceId];

    onDietaryPreferencesChange(updatedPreferences);
  };
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {t("menuItems.dietaryPreferences")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dietaryPreferences.map((preference) => {
            const isSelected = selectedDietaryPreferences.includes(preference.id);

            return (
              <button
                key={preference.id}
                type="button"
                onClick={(e) => handleToggleDietaryPreference(e, preference.id)}
                className={`p-4 rounded-lg border-2 transition-all ${isRTL ? 'text-right' : 'text-left'} ${isSelected
                  ? "border-primary bg-primary/10 dark:bg-primary/20"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
              >
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${isSelected
                      ? "border-primary bg-primary"
                      : "border-gray-300 dark:border-gray-600"
                      }`}
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {getDisplayDietaryName(preference, i18n.language)}
                    </p>
                    {getDisplayDietaryDescription(preference, i18n.language) && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {getDisplayDietaryDescription(preference, i18n.language)}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DietaryPreferencesTab;
